import { FC, useState, useRef, useCallback } from "react";
import { Box, Fade, Typography } from "@mui/material";
import { makeSxStyles } from "ui";

export interface RegionDriverStartingPointDetailsProps {
  driverFullName?: string;
  driverId?: string;
  driverStartingPointAddress?: string;
}

interface RegionDriverStartingPointTooltipProps {
  isHovering: boolean;
  position: { x: number; y: number };
  driverFullName?: string;
  driverStartingPointAddress?: string;
}

export const RegionDriverStartingPointTooltip: FC<
  RegionDriverStartingPointTooltipProps
> = ({ isHovering, position, driverFullName, driverStartingPointAddress }) => {
  const styles = useStyles();

  if (!driverFullName) return null;

  return (
    <Fade in={isHovering} timeout={300}>
      <Box
        sx={{
          ...styles.hoverBox,
          left: position.x,
          top: position.y - 16,
          transform: "translate(-50%, -100%)",
          position: "fixed",
        }}
      >
        <Typography sx={styles.tooltipText}>
          {driverStartingPointAddress || "Driver Starting Point"}
        </Typography>
        <Typography sx={styles.driverName}>{driverFullName}</Typography>
      </Box>
    </Fade>
  );
};

const RegionDriverStartingPointDetails: FC<
  RegionDriverStartingPointDetailsProps
> = (props) => {
  const { driverFullName, driverId, driverStartingPointAddress } = props;
  const styles = useStyles();
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  // Use refs to track position without causing re-renders
  const currentPositionRef = useRef({ x: 0, y: 0 });
  const rafIdRef = useRef<number | null>(null);

  // Don't show tooltip for "any", "NO_DRIVER", "No Driver", or undefined/empty driver
  const shouldShowTooltip =
    driverFullName &&
    driverId &&
    driverId !== "NO_DRIVER" &&
    driverId !== "No Driver" &&
    driverId !== "Any" &&
    driverFullName !== "No Driver" &&
    driverFullName !== "Any";

  // Throttled position update using requestAnimationFrame
  const updatePosition = useCallback(() => {
    setHoverPosition({
      x: currentPositionRef.current.x,
      y: currentPositionRef.current.y,
    });
    rafIdRef.current = null;
  }, []);

  const handleMouseEnter = (event: React.MouseEvent) => {
    if (!shouldShowTooltip) return;
    currentPositionRef.current = { x: event.clientX, y: event.clientY };
    setHoverPosition({ x: event.clientX, y: event.clientY });
    setIsHovering(true);
  };

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!shouldShowTooltip) return;

      // Update the ref immediately (no re-render)
      currentPositionRef.current = { x: event.clientX, y: event.clientY };

      // Throttle the state update using requestAnimationFrame
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(updatePosition);
      }
    },
    [shouldShowTooltip, updatePosition],
  );

  const handleMouseLeave = useCallback(() => {
    // Cancel any pending animation frame
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    setIsHovering(false);
  }, []);

  return (
    <>
      <Box
        sx={{
          ...styles.addressWrapper,
          cursor: shouldShowTooltip ? "pointer" : "default",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <Box sx={styles.startingPoint} />
        <Box sx={styles.startingPointLine} />
      </Box>
      {shouldShowTooltip && (
        <RegionDriverStartingPointTooltip
          isHovering={isHovering}
          position={hoverPosition}
          driverFullName={driverFullName}
          driverStartingPointAddress={driverStartingPointAddress}
        />
      )}
    </>
  );
};

const useStyles = makeSxStyles((theme) => ({
  addressWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  startingPoint: {
    borderRadius: "100%",
    width: "8px",
    height: "8px",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  startingPointLine: {
    content: '""',
    width: "4px",
    height: "1px",
    display: "inline-flex",
    backgroundColor: "#fff",
  },
  tooltipText: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 600,
    color: "rgba(138, 206, 255, 1)",
    mb: "4px",
    maxWidth: "240px",
  },
  driverName: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 500,
    color: "#fff",
  },
  hoverBox: {
    backgroundColor: theme.palette.grey["600"],
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.06)",
    padding: "16px",
    borderRadius: "12px",
    zIndex: 1001,
    minWidth: "240px",
    maxWidth: "400px",
    minHeight: "80px",

    "&::before": {
      content: '""',
      position: "absolute",
      width: 0,
      height: 0,
      borderLeft: "8px solid transparent",
      borderRight: "8px solid transparent",
      borderTop: "8px solid #282A2D",
      bottom: "-8px",
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
}));

export default RegionDriverStartingPointDetails;
